import { useHandler } from "@redotech/react-util/hook";
import { SegmentListMultipleItemMembershipOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoProductMetafieldsSegmentSchema,
  RcoProductMetafieldsSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { OperatorSelector } from "@redotech/redo-web-conversion/conversion/operator-selector";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { ProductMetafieldsSelector } from "../../common/product-metafields-selector";

export const ProductMetafieldsInspectorContent = memo(
  function ProductMetafieldsInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoProductMetafieldsSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(segment.cartOperator, segment.operator)}
          </Text>
        </Flex>
        <Flex dir="column" gap="md" w="full">
          {segment.metafields.map((metafield, index) => (
            <Flex dir="row" gap="sm" key={index} wrap="wrap">
              <RedoBadge color="blue" size="sm" text={metafield.resourceType} />
              <RedoBadge
                color="gray"
                size="sm"
                text={`${metafield.namespace}.${metafield.key}`}
              />
              <RedoBadge
                color="gray"
                size="sm"
                text={`${metafield.value}${metafield.valueMatchKind === "partial" ? " (contains)" : ""}`}
              />
            </Flex>
          ))}
        </Flex>
      </>
    );
  },
);

const operatorsToText = (
  cartOperator: RcoProductMetafieldsSegmentSchema["cartOperator"],
  operator: RcoProductMetafieldsSegmentSchema["operator"],
) => {
  switch (cartOperator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return t(
            "Any item in the cart has all of the metafields in the list:",
          );
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return t(
            "Any item in the cart has at least one metafield in the list:",
          );
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return t(
            "Any item in the cart does not have any metafield in the list:",
          );
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.ALL_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return t(
            "All products/variants in the cart have all of the metafields in the list:",
          );
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return t(
            "All products/variants in the cart have at least one metafield in the list:",
          );
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return t(
            "All products/variants in the cart do not have any metafield in the list",
          );
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      switch (operator) {
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return t(
            "No item in the cart has all of the metafields in the list:",
          );
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return t(
            "No item in the cart has at least one metafield in the list:",
          );
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return t(
            "No item in the cart does not have any metafield in the list:",
          );
        default:
          assertNever(operator);
      }
      break;
    default:
      assertNever(cartOperator);
  }
};

export function segmentListMultipleItemMembershipOperatorToText(): Record<
  SegmentListMultipleItemMembershipOperator,
  string
> {
  return {
    [SegmentListMultipleItemMembershipOperator.ANY_IN]: t("Any"),
    [SegmentListMultipleItemMembershipOperator.NONE_IN]: t("No"),
    [SegmentListMultipleItemMembershipOperator.ALL_IN]: t("Every"),
  };
}

export function productMetafieldsSegmentListMultipleItemMembershipOperatorToText(): Record<
  SegmentListMultipleItemMembershipOperator,
  string
> {
  return {
    [SegmentListMultipleItemMembershipOperator.ALL_IN]: t("every"),
    [SegmentListMultipleItemMembershipOperator.ANY_IN]: t("any"),
    [SegmentListMultipleItemMembershipOperator.NONE_IN]: t("no"),
  };
}

export const EditableProductMetafieldsInspectorContent = memo(
  function EditableProductMetafieldsInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoProductMetafieldsSegmentSchema.parse(step.segment),
      [step],
    );

    const handleCartOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        setStep({ ...step, segment: { ...segment, cartOperator: operator } });
      },
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        setStep({ ...step, segment: { ...segment, operator } });
      },
    );

    const handleMetafieldsChanged = useHandler(
      (metafields: RcoProductMetafieldsSegmentSchema["metafields"]) => {
        const updatedSegment = { ...segment, metafields };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Flex>
            <OperatorSelector<SegmentListMultipleItemMembershipOperator>
              availableOptions={Object.values(
                SegmentListMultipleItemMembershipOperator,
              )}
              onOperatorSelected={handleCartOperatorSelected}
              operatorToText={segmentListMultipleItemMembershipOperatorToText()}
              selectedOperator={segment.cartOperator}
            />
          </Flex>
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("item in the cart has")}
        </Text>
        <Flex>
          <OperatorSelector<SegmentListMultipleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListMultipleItemMembershipOperator,
            )}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={productMetafieldsSegmentListMultipleItemMembershipOperatorToText()}
            selectedOperator={segment.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("metafield in the list:")}
        </Text>
        <ProductMetafieldsSelector
          metafields={segment.metafields}
          onMetafieldsChanged={handleMetafieldsChanged}
        />
      </>
    );
  },
);
